import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

import { Tick } from './parts.js';

const EASE = [0.16, 1, 0.3, 1];

/* ─────────────────────────────────────────────────────────────
   Scenes: one tap that sets the residence's lights, shades, climate
   and music together. The Scenes button in the bar opens them, and
   the quick menu carries them too, since a phone hides that button.

   `swatch` is the scene's light, drawn rather than photographed, and
   the same colour the backdrop drifts toward in design/actions.css
   once the scene is on.
   ───────────────────────────────────────────────────────────── */
export const SCENES = [
    {
        key: 'morning', name: 'Morning', hours: '6 to 10 AM',
        summary: 'Shades open, lights at 70% warm white, 72°F',
        systems: ['Lights 70%', 'Shades open', '72°F', 'Coffee bar on'],
        swatch: 'radial-gradient(circle at 50% 85%, #fff1c9 0%, #ffbf8a 32%, #c77fa6 68%, #3d4f8f 100%)'
    },
    {
        key: 'daytime', name: 'Daytime', hours: '10 AM to 5 PM',
        summary: 'Natural light, shades follow the sun, 73°F',
        systems: ['Lights off', 'Shades auto', '73°F', 'Pool deck music'],
        swatch: 'radial-gradient(circle at 50% 30%, #ffffff 0%, #c9f1ff 30%, #6fc3ea 70%, #2b6e9e 100%)'
    },
    {
        key: 'evening', name: 'Evening', hours: '5 to 10 PM',
        summary: 'Lights at 40% amber, shades half closed, lounge playlist',
        systems: ['Lights 40%', 'Shades half', '72°F', 'Lounge playlist'],
        swatch: 'radial-gradient(circle at 50% 90%, #ffd08a 0%, #ff8a4c 30%, #a6406b 66%, #2a1d45 100%)'
    },
    {
        key: 'party', name: 'Party', hours: 'Any time',
        summary: 'Color lighting, music up, terrace and bar lights on',
        systems: ['Color wash', 'Shades closed', '70°F', 'Music 70%'],
        swatch: 'conic-gradient(from 200deg, #ff4fd8, #8a5cff, #3fd0ff, #ffb84f, #ff4fd8)'
    },
    {
        key: 'night', name: 'Night', hours: '10 PM to 6 AM',
        summary: 'Path lighting at 10%, shades closed, doors locked',
        systems: ['Path lights 10%', 'Shades closed', '68°F', 'Doors locked'],
        swatch: 'radial-gradient(circle at 68% 30%, #e9f0ff 0%, #e9f0ff 9%, #3b4f8f 12%, #161d3d 60%, #070912 100%)'
    },
    {
        key: 'away', name: 'Away', hours: 'While you are out',
        summary: 'Everything off, shades closed, 78°F, security watch on',
        systems: ['All lights off', 'Shades closed', '78°F', 'Security on'],
        swatch: 'radial-gradient(circle at 50% 40%, #8b8f99 0%, #4a4d55 45%, #1c1d21 100%)'
    }
];

export const sceneOf = key => SCENES.find(s => s.key === key) || null;

/* The scene cards. `applying` is the one mid-change: its card fills a
   bar while the house catches up, so the tap has a visible "doing it"
   before the "done". `compact` is the quick menu's denser row. */
export function ScenePicker({ active, applying, onApply, compact = false }) {
    const reduced = useReducedMotion();

    return (
        <div className={`scenes${compact ? ' scenes--compact' : ''}`} role="group" aria-label="Scenes">
            {SCENES.map((scene, i) => {
                const on = active === scene.key;
                const busy = applying === scene.key;

                return (
                    <motion.button
                        type="button"
                        key={scene.key}
                        className={`scene${on ? ' scene--on' : ''}${busy ? ' scene--busy' : ''}`}
                        aria-pressed={on}
                        disabled={Boolean(applying) && !busy}
                        onClick={() => onApply(scene.key)}
                        initial={reduced ? false : { opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.05 + i * 0.04, ease: EASE }}
                    >
                        <span className="scene__swatch" style={{ background: scene.swatch }} aria-hidden="true" />
                        <span className="scene__body">
                            <span className="scene__name">
                                {scene.name}
                                {on && <span className="scene__on"><Tick /> On</span>}
                            </span>
                            {!compact && <span className="scene__hours">{scene.hours}</span>}
                            {!compact && (
                                <span className="scene__systems">
                                    {/* A no-break space holds each dot to the item
                                        before it, so a wrapped line never opens
                                        with a separator. */}
                                    {scene.systems.join('\u00a0· ')}
                                </span>
                            )}
                        </span>
                        {busy && (
                            <span className="scene__progress" aria-hidden="true">
                                <span />
                            </span>
                        )}
                    </motion.button>
                );
            })}
        </div>
    );
}
